/* Produktseite des Weissblau-Themes: Galerie, Variantenwechsel, Menge, Akkordeons,
 * Groessentabellen-Popup, Sticky-Kaufleiste und das Nachladen von "Passt dazu".
 *
 * Eigene Umsetzung mit eigenen Namen - keine Kopplung an fremde Elementnamen.
 *
 * Was hier bewusst so ist (Quellen: docs/relaunch/02 und 08):
 * - Der Variantenwechsel aktualisiert Preis, Vergleichspreis, Grundpreis, Verfuegbarkeit, Bild
 *   und die ?variant=-URL, setzt das versteckte Feld und loest darauf ein change-Ereignis aus.
 *   Genau darauf hoeren "Haeufig zusammen gekauft" und die B2B-Box (02 Abschnitt 18). Zusaetzlich
 *   gibt es das eigene Ereignis mhsys:variante.
 * - Die Werte kommen aus einem JSON-Block, den Liquid geschrieben hat. Preis, Grundpreis und
 *   Verfuegbarkeit werden NICHT in JavaScript nachgebaut (02 Abschnitt 9).
 * - Die URL bekommt ?variant=<id>, nicht ?option_values= - das alte Theme schrieb Letzteres, und
 *   Bausteine, die die Variante aus der URL lasen, lagen daneben (08 D10).
 * - Die Kaufleiste haengt an EINEM IntersectionObserver. Mehrfach registrierte Scroll-Listener
 *   waren im alten Theme ein eigener Fehler (08 D13), und ein Scroll-Handler, der Geometrie liest,
 *   hat dort eine Rueckkopplung erzeugt (08 E11).
 * - Die Kaufleiste sendet das Hauptformular ab und hat kein eigenes - so kann sie nie eine andere
 *   Variante einlegen als die angezeigte.
 */
(() => {
  'use strict';

  /* Texte kommen aus den Sprachdateien und stehen im Layout in window.mhsysTexte.
     Hier wird nichts uebersetzt und nichts fest verdrahtet. */
  const text = (schluessel, rueckfall) =>
    (window.mhsysTexte && window.mhsysTexte[schluessel]) || rueckfall;

  /* ------------------------------------------------------------------ Galerie */
  class MhsysGalerie {
    constructor(wurzel) {
      this.wurzel = wurzel;
      this.bilder = [...wurzel.querySelectorAll('[data-mhsys-galerie-bild]')];
      this.thumbs = [...wurzel.querySelectorAll('[data-mhsys-galerie-thumb]')];
      this.buehne = wurzel.querySelector('[data-mhsys-galerie-buehne]');
      this.zoomKnopf = wurzel.querySelector('[data-mhsys-galerie-zoom]');
      this.zoomText = wurzel.querySelector('[data-mhsys-galerie-zoomtext]');
      this.spur = wurzel.querySelector('[data-mhsys-galerie-spur]');
      this.punkte = [...wurzel.querySelectorAll('[data-mhsys-galerie-punkt]')];
      this.zaehler = wurzel.querySelector('[data-mhsys-galerie-zaehler]');
      this.aktiv = 0;

      this.thumbs.forEach((t) =>
        t.addEventListener('click', () => this.zeigen(Number(t.dataset.mhsysGalerieThumb)))
      );
      this.punkte.forEach((p) =>
        p.addEventListener('click', () => this.zeigen(Number(p.dataset.mhsysGaleriePunkt)))
      );

      if (this.zoomKnopf) {
        this.zoomKnopf.addEventListener('click', () => this.zoomen());
      }
      if (this.buehne) {
        this.buehne.addEventListener('click', (e) => {
          if (e.target.closest('[data-mhsys-galerie-zoom]')) return;
          if (e.target.closest('[data-mhsys-galerie-punkt]')) return;
          /* Tippen/Klicken aufs Bild oeffnet das Vollbild - am Handy und am Desktop (Inhaber 18.09.2026).
             Wischen loest kein click aus, das Blaettern in der Spur bleibt also ungestoert. */
          if (!e.target.closest('[data-mhsys-galerie-bild]')) return;
          this.zoomen();
        });
      }

      /* Welches Bild beim Wischen gerade zu sehen ist: EIN IntersectionObserver auf die Spur,
         kein Scroll-Handler (08 D13, E11). Am Desktop sind nur aktive Bilder sichtbar, dort
         meldet er nur, was ohnehin gesetzt ist. */
      if (this.spur && this.bilder.length > 1 && typeof IntersectionObserver !== 'undefined') {
        const beobachter = new IntersectionObserver(
          (eintraege) => {
            eintraege.forEach((e) => {
              if (e.isIntersecting) this.markieren(this.bilder.indexOf(e.target));
            });
          },
          { root: this.spur, threshold: 0.6 }
        );
        this.bilder.forEach((b) => beobachter.observe(b));
      }

      this.vorschauVerbinden();
      this.spaeteBilderVerbinden();
      this.wischHinweis();
    }

    /* Folgebilder tragen src/srcset nur als data-* (snippets/mhsys-galerie.liquid, Roadmap 6.10): am Handy lagen sie in
       Chromes Vorlade-Abstand und stritten mit dem LCP-Bild um die Leitung. Echt werden sie nach window.load (in einer
       nachgeladenen Schnellansicht sofort), beim ersten Beruehren der Spur oder wenn eines gezeigt werden soll
       (markieren). Das Anstupsen des Wisch-Hinweises zaehlt nicht - es ist kein Wunsch, ein Bild zu sehen. */
    spaeteBilderVerbinden() {
      this.spaet = this.bilder.filter((b) => b.hasAttribute('data-mhsys-galerie-spaet'));
      if (!this.spaet.length) return;
      const holen = () => this.spaeteBilderHolen();
      if (document.readyState === 'complete') holen();
      else window.addEventListener('load', holen, { once: true });
      if (this.spur) {
        this.spur.addEventListener('pointerdown', holen, { once: true, passive: true });
        this.spur.addEventListener('touchstart', holen, { once: true, passive: true });
      }
    }

    spaeteBilderHolen() {
      if (!this.spaet || !this.spaet.length) return;
      this.spaet.forEach((img) => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.src) img.src = img.dataset.src;
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
        img.removeAttribute('data-mhsys-galerie-spaet');
      });
      this.spaet = [];
    }

    /* Einmal je Sitzung stupst die Spur am Handy kurz an (30 px hin und zurueck) - zusammen mit dem
       hereinschauenden Nachbarbild das Zeichen, dass man wischen kann. Nicht bei reduzierter Bewegung,
       nicht, wenn schon gewischt oder getippt wurde, nicht im Hintergrund-Tab. */
    wischHinweis() {
      if (!this.spur || this.bilder.length < 2 || !this.wischbar()) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const SCHLUESSEL = 'mhsysWischHinweis';
      try {
        if (sessionStorage.getItem(SCHLUESSEL)) return;
      } catch (e) {
        return;
      }
      let beruehrt = false;
      const stopp = () => (beruehrt = true);
      this.spur.addEventListener('pointerdown', stopp, { once: true, passive: true });
      this.spur.addEventListener('touchstart', stopp, { once: true, passive: true });
      const los = () => {
        if (beruehrt || document.visibilityState !== 'visible' || this.spur.scrollLeft > 0) return;
        try {
          sessionStorage.setItem(SCHLUESSEL, '1');
        } catch (e) {
          return;
        }
        this.spur.style.scrollSnapType = 'none';
        this.spur.scrollTo({ left: 30, behavior: 'smooth' });
        window.setTimeout(() => {
          if (!beruehrt) this.spur.scrollTo({ left: 0, behavior: 'smooth' });
          window.setTimeout(() => (this.spur.style.scrollSnapType = ''), 450);
        }, 450);
      };
      window.setTimeout(los, 1200);
    }

    /* Vorschau-Reihe: EINE Reihe, laeuft sie ueber, Pfeile links/rechts (nur Desktop sichtbar, am
       Anfang/Ende ausgeblendet). Welche Enden zu sehen sind, meldet EIN IntersectionObserver auf dem
       ersten und letzten Vorschaubild - kein Scroll-Handler (08 D13, E11). */
    vorschauVerbinden() {
      this.vSpur = this.wurzel.querySelector('[data-mhsys-vorschau-spur]');
      if (!this.vSpur || this.thumbs.length < 2) return;
      const zurueck = this.wurzel.querySelector('[data-mhsys-vorschau-zurueck]');
      const weiter = this.wurzel.querySelector('[data-mhsys-vorschau-weiter]');
      const ruhig = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const blaettern = (richtung) =>
        this.vSpur.scrollBy({ left: richtung * this.vSpur.clientWidth * 0.8, behavior: ruhig() ? 'auto' : 'smooth' });
      if (zurueck) zurueck.addEventListener('click', () => blaettern(-1));
      if (weiter) weiter.addEventListener('click', () => blaettern(1));

      /* Sofort einmal: der Beobachter meldet erst, wenn der Tab zeichnet */
      if (weiter) weiter.hidden = this.vSpur.scrollWidth <= this.vSpur.clientWidth + 1;
      if (typeof IntersectionObserver === 'undefined') return;
      const erstes = this.thumbs[0];
      const letztes = this.thumbs[this.thumbs.length - 1];
      const ende = new IntersectionObserver(
        (eintraege) => {
          eintraege.forEach((e) => {
            const ganz = e.intersectionRatio > 0.95;
            if (e.target === erstes && zurueck) zurueck.hidden = ganz;
            if (e.target === letztes && weiter) weiter.hidden = ganz;
          });
        },
        { root: this.vSpur, threshold: [0, 0.95, 1] }
      );
      ende.observe(erstes);
      ende.observe(letztes);
    }

    /* Das aktive Vorschaubild in den sichtbaren Teil der Reihe holen - nur die Reihe scrollt, nie die
       Seite (kein scrollIntoView) */
    vorschauZeigen(index) {
      const s = this.vSpur;
      const t = this.thumbs[index];
      if (!s || !t || s.scrollWidth <= s.clientWidth + 1) return;
      const rand = 12;
      const links = t.offsetLeft;
      const rechts = links + t.offsetWidth;
      let ziel = null;
      if (links - rand < s.scrollLeft) ziel = links - rand;
      else if (rechts + rand > s.scrollLeft + s.clientWidth) ziel = rechts + rand - s.clientWidth;
      if (ziel === null) return;
      const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      s.scrollTo({ left: Math.max(0, ziel), behavior: ruhig ? 'auto' : 'smooth' });
    }

    /* Die Spur ist nur auf dem Handy wischbar (CSS); die Frage stellt sich bei jedem Aufruf neu,
       damit ein gedrehtes Tablet richtig antwortet. */
    wischbar() {
      return !!this.spur && getComputedStyle(this.spur).overflowX !== 'visible';
    }

    /* Nur die Anzeige: aktives Bild, Vorschaubild, Punkt und Zaehler. Scrollt nicht. */
    markieren(index) {
      if (index < 0) return;
      if (index > 0) this.spaeteBilderHolen();
      this.aktiv = index;
      this.bilder.forEach((b, i) => b.classList.toggle('is-aktiv', i === index));
      this.thumbs.forEach((t, i) => {
        const aktiv = i === index;
        t.classList.toggle('is-aktiv', aktiv);
        t.setAttribute('aria-pressed', aktiv ? 'true' : 'false');
      });
      this.punkte.forEach((p, i) => {
        const aktiv = i === index;
        p.classList.toggle('is-aktiv', aktiv);
        if (aktiv) p.setAttribute('aria-current', 'true');
        else p.removeAttribute('aria-current');
      });
      if (this.zaehler) this.zaehler.textContent = `${index + 1} / ${this.bilder.length}`;
      this.vorschauZeigen(index);
    }

    zeigen(index) {
      this.markieren(index);
      if (this.wischbar()) {
        const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        /* Die Bilder sind am Handy schmaler als die Spur (das naechste schaut herein) - daher die
           Lage des Bildes selbst, nicht Index x Breite */
        const bild = this.bilder[index];
        const links = bild ? bild.getBoundingClientRect().left - this.spur.getBoundingClientRect().left + this.spur.scrollLeft : 0;
        this.spur.scrollTo({ left: links, behavior: ruhig ? 'auto' : 'smooth' });
      }
      this.zoomAus();
    }

    /* Zum Bild einer Variante springen. Der Schluessel ist die Medien-ID, nicht die Varianten-ID:
       mehrere Varianten koennen sich dasselbe Bild teilen. */
    zuMedium(medienId) {
      if (!medienId) return;
      const treffer = this.bilder.findIndex((b) => Number(b.dataset.mhsysMedium) === Number(medienId));
      if (treffer >= 0) this.zeigen(treffer);
    }

    /* Vollbild: ein <dialog> mit allen Bildern in grosser Aufloesung in einer Wischspur (scroll-snap).
       Handy: wischen, mit zwei Fingern zoomen. Desktop: Pfeile/Pfeiltasten. Schliessen mit X oder Esc
       (Esc macht der Dialog selbst). Erst beim ersten Oeffnen gebaut - kostet sonst nichts. */
    zoomen() {
      if (!this.vollbild) this.vollbildBauen();
      const d = this.vollbild;
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
      document.documentElement.classList.add('mhsys-vollbild-offen');
      const spur = d.querySelector('.mhsys-vollbild__spur');
      spur.scrollLeft = this.aktiv * spur.clientWidth;
      this.vollbildZaehlen();
    }

    vollbildBauen() {
      const d = document.createElement('dialog');
      d.className = 'mhsys-vollbild';
      d.setAttribute('aria-label', 'Produktbilder im Vollbild');
      const spur = document.createElement('div');
      spur.className = 'mhsys-vollbild__spur';
      this.bilder.forEach((b) => {
        const img = document.createElement('img');
        img.className = 'mhsys-vollbild__bild';
        img.src = b.dataset.mhsysGross || b.currentSrc || b.src;
        img.alt = b.alt || '';
        img.decoding = 'async';
        spur.appendChild(img);
      });
      const knopf = (klasse, label, zeichen) => {
        const k = document.createElement('button');
        k.type = 'button';
        k.className = 'mhsys-vollbild__knopf ' + klasse;
        k.setAttribute('aria-label', label);
        k.textContent = zeichen;
        return k;
      };
      /* Schliessen wie an jedem Overlay (Roadmap 4.14): mhsys-ueberlage__zu, auf dem Foto in der weissen Fassung */
      const zu = knopf('mhsys-vollbild__zu mhsys-ueberlage__zu mhsys-ueberlage__zu--foto', 'Schließen', '');
      zu.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M5.05 3.9c.3 0 .6.12.83.35L12 10.37l6.12-6.12a1.17 1.17 0 0 1 1.66 1.66L13.66 12l6.12 6.12a1.17 1.17 0 0 1-1.66 1.66L12 13.66l-6.12 6.12a1.17 1.17 0 0 1-1.66-1.66L10.34 12 4.22 5.88A1.17 1.17 0 0 1 5.05 3.9Z"/></svg>';
      const zurueck = knopf('mhsys-vollbild__pfeil mhsys-vollbild__pfeil--zurueck', 'Vorheriges Bild', '‹');
      const vor = knopf('mhsys-vollbild__pfeil mhsys-vollbild__pfeil--vor', 'Nächstes Bild', '›');
      const zahl = document.createElement('div');
      zahl.className = 'mhsys-vollbild__zahl';
      d.append(spur, zu, zahl);
      if (this.bilder.length > 1) d.append(zurueck, vor);
      else zahl.hidden = true;

      const blaettern = (richtung) => spur.scrollBy({ left: richtung * spur.clientWidth, behavior: 'smooth' });
      /* Aufraeumen sofort beim Schliessen, nicht erst im close-Ereignis - das kommt verzoegert als eigene Aufgabe */
      const schliessen = () => {
        d.close();
        document.documentElement.classList.remove('mhsys-vollbild-offen');
      };
      zu.addEventListener('click', schliessen);
      d.addEventListener('cancel', () => document.documentElement.classList.remove('mhsys-vollbild-offen'));
      zurueck.addEventListener('click', () => blaettern(-1));
      vor.addEventListener('click', () => blaettern(1));
      d.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') blaettern(-1);
        if (e.key === 'ArrowRight') blaettern(1);
      });
      /* Klick neben das Bild schliesst - wie bei jedem Bildbetrachter */
      spur.addEventListener('click', (e) => {
        if (e.target === spur) schliessen();
      });
      /* Am Handy ist das Vollbild ein Schuber von unten (Roadmap 1.78): ein Tipp auf den Schleier darueber schliesst */
      d.addEventListener('click', (e) => {
        if (e.target === d) schliessen();
      });
      /* Direkt im scroll-Ereignis zaehlen: nur eine Division, und rAF laeuft in einem Hintergrund-Tab nicht */
      spur.addEventListener('scroll', () => this.vollbildZaehlen(), { passive: true });
      d.addEventListener('close', () => {
        document.documentElement.classList.remove('mhsys-vollbild-offen');
        /* Die Galerie zeigt danach das Bild, bei dem man im Vollbild stand */
        if (this.vollbildIndex !== undefined) this.zeigen(this.vollbildIndex);
      });
      document.body.appendChild(d);
      this.vollbild = d;
    }

    vollbildZaehlen() {
      const spur = this.vollbild.querySelector('.mhsys-vollbild__spur');
      const i = Math.round(spur.scrollLeft / Math.max(1, spur.clientWidth));
      this.vollbildIndex = Math.min(Math.max(i, 0), this.bilder.length - 1);
      const zahl = this.vollbild.querySelector('.mhsys-vollbild__zahl');
      if (zahl) zahl.textContent = (this.vollbildIndex + 1) + ' / ' + this.bilder.length;
    }

    zoomAus() {
      if (!this.wurzel.classList.contains('is-zoom')) return;
      this.wurzel.classList.remove('is-zoom');
      if (this.zoomKnopf) this.zoomKnopf.setAttribute('aria-pressed', 'false');
      if (this.zoomText) this.zoomText.textContent = 'Zum Zoomen tippen';
    }
  }

  /* ------------------------------------------------------------- Variantenwahl */
  class MhsysVarianten {
    constructor(seite, galerie) {
      this.seite = seite;
      this.galerie = galerie;
      /* Schnellansicht (Roadmap 3.25): dieselbe Variantenwahl in einem Overlay auf fremder Seite -
         dort darf die Adresse kein ?variant= bekommen */
      this.schnellansicht = seite.hasAttribute('data-mhsys-schnellansicht-produkt');
      this.bereich = seite.querySelector('[data-mhsys-varianten]');
      this.feld = seite.querySelector('[data-mhsys-variante-feld]');
      if (!this.bereich || !this.feld) return;

      const daten = this.bereich.querySelector('[data-mhsys-varianten-daten]');
      try {
        this.varianten = JSON.parse(daten.textContent);
      } catch (e) {
        console.error('Varianten konnten nicht gelesen werden:', e);
        return;
      }

      this.eingaben = [...this.bereich.querySelectorAll('[data-mhsys-option-eingabe]')];
      this.eingaben.forEach((el) => el.addEventListener('change', () => this.wechseln()));
      this.bereit = true;
      /* Beim Laden einmal den Zustand herstellen (ausverkaufte Kombinationen markieren) */
      this.lieferbarkeitMarkieren();
      /* Link mit ?variant= (Google Shopping, Werbung): die Auswahl stimmte schon, die Galerie zeigte
         aber das erste Bild - wer das navy Shirt anklickt, sah ein graues (Pruefung 18.09.2026). */
      if (this.galerie && new URLSearchParams(location.search).has('variant')) {
        const start = this.varianten.find((v) => String(v.id) === String(this.feld.value));
        if (start) this.galerie.zuMedium(start.bild);
      }
    }

    gewaehlteWerte() {
      const werte = [];
      this.bereich.querySelectorAll('[data-mhsys-option-eingabe]').forEach((el) => {
        if (el.checked) {
          const pos = Number(el.dataset.mhsysOptionEingabe) - 1;
          werte[pos] = el.value;
        }
      });
      return werte;
    }

    /* Alle gesetzten Werte muessen passen. Leere Stellen (noch nichts gewaehlt) zaehlen nicht mit. */
    passende(werte) {
      return this.varianten.filter((v) =>
        werte.every((w, i) => w == null || v.optionen[i] === w)
      );
    }

    findeVariante(werte) {
      return this.varianten.find((v) => v.optionen.every((o, i) => o === werte[i]));
    }

    /* Werte markieren, die es in Kombination mit der uebrigen Auswahl nicht lieferbar gibt.
       "Vergriffen" heisst: mit dieser Auswahl gibt es zu diesem Wert keine lieferbare Variante. */
    lieferbarkeitMarkieren() {
      const werte = this.gewaehlteWerte();
      this.bereich.querySelectorAll('[data-mhsys-option-eingabe]').forEach((el) => {
        const pos = Number(el.dataset.mhsysOptionEingabe) - 1;
        const probe = werte.slice();
        probe[pos] = el.value;
        const moeglich = this.passende(probe).some((v) => v.lieferbar);
        const knopf = el.nextElementSibling;
        if (knopf) knopf.classList.toggle('is-vergriffen', !moeglich);
      });
    }

    wechseln() {
      const werte = this.gewaehlteWerte();
      const variante = this.findeVariante(werte);
      this.lieferbarkeitMarkieren();

      /* Name der gewaehlten Farbe neben der Ueberschrift nachziehen */
      this.bereich.querySelectorAll('[data-mhsys-option-wert]').forEach((el) => {
        const pos = Number(el.dataset.mhsysOptionWert) - 1;
        if (werte[pos]) el.textContent = werte[pos];
      });

      if (!variante) {
        this.knopfSperren(text('nichtVerfuegbar', 'Nicht verfügbar'));
        return;
      }

      this.feld.value = variante.id;
      /* change am versteckten Feld: darauf hoeren Produktempfehlungen und B2B-Box */
      this.feld.dispatchEvent(new Event('change', { bubbles: true }));

      this.preiseSetzen(variante);
      this.knopfSetzen(variante);
      this.mengeBegrenzen(variante);
      if (this.galerie) this.galerie.zuMedium(variante.bild);
      this.urlSetzen(variante);
      this.lagerNachziehen(variante);

      /* Eigenes Ereignis des Themes mit der vollstaendigen Variante */
      document.dispatchEvent(
        new CustomEvent('mhsys:variante', { detail: { variante: variante, feld: this.feld } })
      );
    }

    preiseSetzen(variante) {
      const preis = this.seite.querySelector('[data-mhsys-preis]');
      const alt = this.seite.querySelector('[data-mhsys-preis-alt]');
      const grund = this.seite.querySelector('[data-mhsys-grundpreis]');
      const leistePreis = document.querySelector('[data-mhsys-leiste-preis]');

      /* Die Betraege kommen fertig formatiert aus Liquid - hier wird nicht gerechnet */
      if (preis) preis.textContent = variante.preis;
      if (leistePreis) leistePreis.textContent = variante.preis;
      if (alt) {
        alt.textContent = variante.vergleichspreis || '';
        alt.hidden = !variante.vergleichspreis;
      }
      /* Grundpreis kommt fertig aus Liquid - hier wird nichts gerechnet */
      if (grund) grund.textContent = variante.grundpreis || '';
      const leisteGrund = document.querySelector('[data-mhsys-leiste-grundpreis]');
      if (leisteGrund) {
        leisteGrund.textContent = variante.grundpreis || '';
        leisteGrund.hidden = !variante.grundpreis;
      }

      const leisteVariante = document.querySelector('[data-mhsys-leiste-variante]');
      if (leisteVariante) {
        /* Variantenname = Preis (Gutschein "25,00 €") nicht doppelt zeigen - gleiche Regel wie
           sections/mhsys-kaufleiste.liquid (ohne Leerzeichen, ",00" zaehlt nicht) */
        const name = variante.optionen.join(' · ');
        const gleich = (t) => String(t || '').replace(/\s/g, '').replace(/,00/g, '').toLowerCase();
        const istPreis = gleich(name) === gleich(variante.preis);
        leisteVariante.textContent = istPreis ? '' : name;
        leisteVariante.hidden = istPreis;
      }
    }

    knopfSetzen(variante) {
      const knopf = this.seite.querySelector('[data-mhsys-kaufknopf]');
      // Nicht "text" nennen: das ueberdeckte die Hilfsfunktion text() oben, der Aufruf
      // darunter warf einen TypeError und brach den ganzen Variantenwechsel ab
      // (Bild, URL und mhsys:variante blieben aus).
      const knopfText = this.seite.querySelector('[data-mhsys-kaufknopf-text]');
      const leiste = document.querySelector('[data-mhsys-leiste-kaufen]');
      const leisteText = document.querySelector('[data-mhsys-leiste-text]');
      const beschriftung = variante.lieferbar
        ? text('inDenWarenkorb', 'In den Warenkorb')
        : text('ausverkauft', 'Ausverkauft');
      /* Die Kaufleiste hat einen eigenen, kuerzeren Text (Roadmap 4.19, Inhaber 23.09.2026) -
         sonst stuende hier nach dem ersten Variantenwechsel wieder der lange. Ausverkauft ist
         in beiden gleich. */
      const beschriftungLeiste = variante.lieferbar
        ? text('inDenWarenkorbLeiste', text('inDenWarenkorb', 'In den Warenkorb'))
        : text('ausverkauft', 'Ausverkauft');

      [knopf, leiste].forEach((k) => {
        if (!k) return;
        k.disabled = !variante.lieferbar;
      });
      if (knopfText) knopfText.textContent = beschriftung;
      if (leisteText) leisteText.textContent = beschriftungLeiste;
    }

    /* Die Obergrenze des Mengenfelds gilt je Variante - Liquid schreibt nur die der Startvariante */
    mengeBegrenzen(variante) {
      const feld = this.seite.querySelector('[data-mhsys-menge-feld]');
      if (!feld || variante.max === undefined) return;
      if (variante.max === null) {
        feld.removeAttribute('max');
      } else {
        /* nie unter die Mindestbestellmenge (Roadmap 3.11): reicht der Bestand nicht, meldet Shopify das beim Einlegen */
        const max = Math.max(Number(feld.min) || 1, variante.max);
        feld.max = max;
        if (Number(feld.value) > max) feld.value = max;
      }
      /* Plus/Minus neu sperren (mengeVerbinden hoert auf change) */
      feld.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Lagerstatus, Express-Box, Lieferzeit und Benachrichtigungsformular rechnet Liquid fuer eine
       Variante. Aendert sich beim Wechsel "auf Lager", wird der Block fuer die neue Variante geholt -
       sonst nicht (bei den meisten Artikeln bleibt es damit bei null Anfragen). Ausnahme: ein Rahmen mit
       data-mhsys-je-variante (Varianten mit eigener Inhaltsliste) wird bei jedem Wechsel geholt. */
    lagerNachziehen(variante) {
      const boxen = [...this.seite.querySelectorAll('[data-mhsys-lagerstatus]')];
      if (!boxen.length || variante.lager === undefined) return;
      const soll = String(variante.lager);
      /* data-mhsys-je-variante: Inhalt haengt an der Variante selbst (Jugendschutz beim Adventskalender) */
      const jeVariante = boxen.some((b) => b.hasAttribute('data-mhsys-je-variante'));
      if (!jeVariante && boxen.every((b) => b.dataset.mhsysLager === soll)) return;
      const section = boxen[0].dataset.mhsysSection;
      if (!section) return;
      boxen.forEach((b) => (b.dataset.mhsysLager = soll));

      const nr = (this.lagerNr = (this.lagerNr || 0) + 1);
      /* In der Schnellansicht ist die Seite der Finder, nicht das Produkt: dort die Produkt-URL */
      const url = new URL(this.seite.dataset.mhsysProduktUrl || window.location.href, window.location.origin);
      url.searchParams.set('variant', variante.id);
      url.searchParams.set('section_id', section);
      fetch(url.toString())
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html || nr !== this.lagerNr) return;
          const neu = [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-mhsys-lagerstatus]')];
          boxen.forEach((box, i) => {
            if (!neu[i] || !document.contains(box)) return;
            box.replaceChildren(...[...neu[i].childNodes].map((n) => document.adoptNode(n)));
            /* Skripte aus adoptNode laufen nicht (Express-Knopf, Benachrichtigungsformular) */
            box.querySelectorAll('script').forEach((alt) => {
              const s = document.createElement('script');
              s.textContent = alt.textContent;
              alt.replaceWith(s);
            });
            /* Links mit data-mhsys-popup ("Mehr Infos" zur Express-Bearbeitung) neu verbinden */
            if (typeof window.mhsysPopupsVerbinden === 'function') window.mhsysPopupsVerbinden(box);
            /* Timeline und Weihnachtslage des neuen Inhalts (assets/mhsys-liefertage.js, Roadmap 6.37) */
            if (window.mhsysLiefertage) window.mhsysLiefertage.starten(box);
            /* Express-Schalter zeigt den Stand des Warenkorbs (Roadmap 1.131) */
            expressSchalterAbgleichen(box);
          });
        })
        .catch((e) => console.error('Lagerstatus:', e));
    }

    knopfSperren(meldung) {
      const knopf = this.seite.querySelector('[data-mhsys-kaufknopf]');
      const knopfText = this.seite.querySelector('[data-mhsys-kaufknopf-text]');
      if (knopf) knopf.disabled = true;
      if (knopfText) knopfText.textContent = meldung;
    }

    urlSetzen(variante) {
      if (this.schnellansicht || !window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variante.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /* -------------------------------------------------------------------- Menge */
  function mengeVerbinden(seite) {
    const feld = seite.querySelector('[data-mhsys-menge-feld]');
    if (!feld) return;
    /* An der Grenze gesperrt wie im Warenkorb - vorher blieb "+" am Bestand aktiv und tat nichts
       (Funktionstest 18.09.2026). Auch nach einem Variantenwechsel (mengeBegrenzen loest change aus). */
    const knoepfeSetzen = () => {
      const wert = Number(feld.value) || 1;
      const min = Number(feld.min) || 1;
      const max = feld.max ? Number(feld.max) : Infinity;
      seite.querySelectorAll('[data-mhsys-menge]').forEach((k) => {
        k.disabled = Number(k.dataset.mhsysMenge) > 0 ? wert >= max : wert <= min;
      });
    };
    /* Von Hand unter die Mindestbestellmenge getippt (Roadmap 3.11): beim Verlassen des Felds zurueck auf sie -
       sonst ginge die kleinere Menge ueber "Mit PayPal zahlen" am Warenkorb vorbei direkt zur Kasse */
    feld.addEventListener('change', () => {
      const min = Number(feld.min) || 1;
      if (!(Number(feld.value) >= min)) feld.value = min; // auch leer: das ginge als 1 hinaus
    });
    feld.addEventListener('change', knoepfeSetzen);
    feld.addEventListener('input', knoepfeSetzen);
    knoepfeSetzen();
    seite.querySelectorAll('[data-mhsys-menge]').forEach((knopf) => {
      knopf.addEventListener('click', () => {
        const schritt = Number(knopf.dataset.mhsysMenge) || 1;
        const min = Number(feld.min) || 1;
        const max = feld.max ? Number(feld.max) : Infinity;
        const neu = Math.min(max, Math.max(min, (Number(feld.value) || min) + schritt));
        feld.value = neu;
        feld.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* --------------------------------------------------------------- Akkordeons */
  function akkordeonsVerbinden(wurzel) {
    wurzel.querySelectorAll('[data-mhsys-akkordeon]').forEach((kopf) => {
      if (kopf.dataset.mhsysBereit) return;
      kopf.dataset.mhsysBereit = '1';
      kopf.addEventListener('click', () => {
        const ziel = document.getElementById(kopf.getAttribute('aria-controls'));
        if (!ziel) return;
        const offen = kopf.getAttribute('aria-expanded') === 'true';
        kopf.setAttribute('aria-expanded', offen ? 'false' : 'true');
        ziel.hidden = offen;
      });
    });
  }

  /* Overlay fuer Seiten (Groessentabelle, "zzgl. Versand", Datenschutzbestimmungen) - das
     Skript zieht jetzt sitewide in assets/mhsys-popup.js, weil auch die Gruenderkarte der
     Startseite denselben Mechanismus braucht (Roadmap 3.3). window.mhsysPopupsVerbinden(wurzel)
     dort verbindet [data-mhsys-groessentabelle]/[data-mhsys-popup] selbst - siehe starten()
     und window.mhsysProduktKauf unten. */

  /* ----------------------------------------------------------- Teilen-Leiste */
  function teilenVerbinden(wurzel) {
    const leiste = wurzel.querySelector('[data-mhsys-teilen]');
    if (!leiste) return;
    const knopf = leiste.querySelector('[data-mhsys-teilen-kopieren]');
    if (!knopf) return;

    const url = leiste.dataset.mhsysTeilenUrl || window.location.href;
    const beschriftung = knopf.querySelector('[data-mhsys-teilen-kopiertext]');
    const ruhetext = beschriftung ? beschriftung.textContent.trim() : '';

    /* Die Zwischenablage gibt es nur in sicheren Kontexten. Steht sie nicht zur Verfuegung,
       bleibt der Knopf weg - ein Knopf, der nichts tut, ist schlimmer als keiner. */
    const kannKopieren = !!(navigator.clipboard && navigator.clipboard.writeText);
    if (!kannKopieren) return;
    knopf.hidden = false;

    let zurueck = null;
    knopf.addEventListener('click', () => {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          if (!beschriftung) return;
          beschriftung.textContent = text('kopiert', 'Link kopiert');
          knopf.classList.add('is-fertig');
          window.clearTimeout(zurueck);
          zurueck = window.setTimeout(() => {
            beschriftung.textContent = ruhetext;
            knopf.classList.remove('is-fertig');
          }, 2000);
        })
        .catch(() => {
          if (beschriftung) beschriftung.textContent = text('kopierenFehlt', 'Kopieren ging nicht');
        });
    });
  }

  /* ------------------------------------------------------------- Kaufleiste */
  function kaufleisteVerbinden(seite) {
    const leiste = document.querySelector('[data-mhsys-kaufleiste]');
    const block = seite.querySelector('[data-mhsys-kaufblock]');
    if (!leiste || !block) return;

    const knopf = leiste.querySelector('[data-mhsys-leiste-kaufen]');
    const formular = seite.querySelector('.mhsys-kauf__formular');
    if (knopf && formular) {
      /* Kein eigenes Formular: die Leiste sendet das Hauptformular ab, damit sie nie eine
         andere Variante einlegt als die angezeigte. */
      /* Den sichtbaren Kaufknopf klicken, nicht requestSubmit(). Der Grund war urspruenglich
         Globo Product Options, das sich an den Klick seines eigenen Knopfs haengte; die Suche
         nach .gpo-clone-atc-button ist mit der Deinstallation weggefallen (23.09.2026, Roadmap
         3.2 Paket D). Geklickt wird weiter statt abgesendet, damit die Leiste denselben Weg
         nimmt wie ein Kunde am Kaufknopf - beide Horcher (Kaufformular und Produktoptionen)
         haengen am submit des Formulars und laufen so wie gewohnt. */
      const sichtbar = (el) => el && !el.hidden && el.offsetParent !== null;
      const absenden = () => {
        const ziel = [...formular.querySelectorAll('[type="submit"]:not([hidden])')].find(sichtbar);
        if (ziel) {
          ziel.click();
          return;
        }
        if (typeof formular.requestSubmit === 'function') formular.requestSubmit();
        else formular.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      };
      /* Am Handy: fehlt eine Angabe (Wunschname, Pflichtauswahl) oder ist die Groesse noch nicht bewusst gewaehlt,
         faehrt der Kauf-Schuber hoch, statt die Seite zum Formular springen zu lassen (Roadmap 1.78) */
      const schuber = kaufSchuberBauen(seite, formular, leiste, absenden);
      knopf.addEventListener('click', () => {
        if (schuber && schuber.noetig()) schuber.oeffnen(knopf);
        else absenden();
      });
    }

    versandHinweisVerbinden(leiste);

    if (typeof IntersectionObserver === 'undefined') return;
    /* Genau ein Observer, kein Scroll-Listener, kein Lesen von Geometrie im Scroll-Handler.
       Der Beobachtungsbereich reicht nach unten weit ueber das Bild hinaus: "im Bereich" heisst also "noch nicht
       nach oben vorbei". Mit dem blossen Bild gab es keinen Wechsel, wenn die Seite in einem Zug ueber den
       Kaufbereich sprang - von unterhalb des Bildes nach oberhalb ist beides "nicht im Bild" (Wunschname-Produkt,
       Kaufbereich beim Laden bei 1204 px; Sprunglink, reduzierte Bewegung): die Kaufleiste blieb weg bzw. stehen
       (Roadmap 1.78, 07.10.2026). */
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        const e = eintraege[eintraege.length - 1];
        const vorbei = !e.isIntersecting && e.boundingClientRect.top < 0;
        leiste.hidden = false;
        leiste.classList.toggle('is-sichtbar', vorbei);
        document.documentElement.classList.toggle('mhsys-kaufleiste-an', vorbei);
      },
      { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
    );
    beobachter.observe(block);
  }

  /* ------------------------------------------------------------- Kauf-Schuber (Roadmap 1.78)
     Inhaber 07.10.2026: "Groesse und Wunschname direkt im Schuber aus der Kaufleiste". Heute sprang "Ins Kerberl
     legen" in der Kaufleiste bei leerem Wunschnamen nach oben zum Feld - die Seite rutschte weg, und man musste
     wieder hinunter. Jetzt faehrt am Handy ein Schuber hoch: Bild, Name und Preis, darunter die Varianten als
     Knoepfe und die Pflichtfelder der Produktoptionen, unten "Ins Kerberl legen".

     Der Schuber hat KEIN eigenes Formular und keine eigene Logik: er ist eine zweite Ansicht auf das Hauptformular.
     - Varianten: eine Kopie der Variantengruppen mit eigenen Namen; ein Tipp klickt das Original (Preis, Bild,
       Verfuegbarkeit und ?variant= macht MhsysVarianten wie immer), danach zieht die Kopie den Zustand nach.
     - Produktoptionen: je Pflichtfeld ein Spiegelfeld; jede Eingabe geht ins Original und loest dort input bzw.
       change aus, die Zeichenpruefung von assets/mhsys-produktoptionen.js laeuft also wie gewohnt, ihre Meldung
       steht gespiegelt im Schuber.
     - Abgeschickt wird wie von der Kaufleiste (absenden). Stellt mhsys-produktoptionen.js die Genitiv-Frage, holt
       der Schuber ihren Kasten zu sich; ihr "Weiter" sendet das Formular wie immer ab.
     - Zu geht er nach dem Einlegen (mhsys:warenkorb-geaendert), mit X, Schleier, Griff, Esc und Zurueck-Taste
       (assets/mhsys-schuber.js). Am Rechner (ab 750 px) bleibt alles beim Alten.
     Wann er noetig ist: ein Pflichtfeld der Produktoptionen ist leer, oder es gibt eine Groessen-Gruppe und auf
     dieser Seite wurde noch keine Groesse angetippt (die Vorauswahl ist nur die erste lieferbare). */
  function kaufSchuberBauen(seite, formular, leiste, absenden) {
    const handy = matchMedia('(max-width: 749px)');
    const varianten = seite.querySelector('[data-mhsys-varianten]');
    const poBox = formular.querySelector('[data-mhsys-po]');
    let poFelder = [];
    if (poBox) {
      try {
        const d = JSON.parse(poBox.dataset.mhsysPoDaten || '{}');
        poFelder = Array.isArray(d.felder) ? d.felder : [];
      } catch (e) {
        poFelder = [];
      }
    }
    const feldBox = (schluessel) => poBox && poBox.querySelector(`[data-mhsys-po-feld="${CSS.escape(schluessel)}"]`);
    // Nur Pflichtfelder, die der Kunde selbst ausfuellt (feste Wahl = verstecktes Feld, hat immer einen Wert)
    const pflichtFelder = poFelder.filter((f) => {
      const box = f.pflicht && feldBox(f.schluessel);
      return box && box.querySelector('input:not([type="hidden"]), textarea, select');
    });
    const gruppen = varianten ? [...varianten.querySelectorAll('[data-mhsys-option]')] : [];
    const istGroesse = (g) =>
      !!g.querySelector('[data-mhsys-groessentabelle]') ||
      /gr(ö|oe|o)(ss|ß)e|size/i.test((g.querySelector('legend') || {}).textContent || '');
    const groessenGruppe = gruppen.find(istGroesse) || null;
    if (!pflichtFelder.length && !groessenGruppe) return null;

    // Eine Groesse gilt als gewaehlt, sobald sie auf dieser Seite angetippt wurde - oder die Adresse eine Variante
    // nennt (Link aus Merkliste, Warenkorb, Werbung: dort wurde schon gewaehlt)
    let groesseGewaehlt = /[?&]variant=\d/.test(window.location.search);
    if (groessenGruppe) {
      groessenGruppe.addEventListener('change', (e) => {
        if (e.isTrusted) groesseGewaehlt = true;
      });
    }

    function leer(f) {
      const box = feldBox(f.schluessel);
      if (!box) return false;
      if (f.typ === 'text' || f.typ === 'textarea') {
        const ein = box.querySelector('.mhsys-po__eingabe');
        return !!ein && ein.value.trim() === '';
      }
      const sel = box.querySelector('select');
      if (sel) return sel.value === '';
      const radios = [...box.querySelectorAll('input[type="radio"]')];
      return radios.length > 0 && !radios.some((r) => r.checked);
    }
    const noetig = () => handy.matches && (pflichtFelder.some(leer) || (!!groessenGruppe && !groesseGewaehlt));

    let el = null;
    let ausloeser = null;
    let seitenY = 0;
    let spiegel = []; // Funktionen, die Kopie/Spiegel aus dem Original nachziehen
    let genitivHerkunft = null; // wo die Genitiv-Frage im Formular stand

    function bauen() {
      el = document.createElement('div');
      el.className = 'mhsys-ks';
      el.hidden = true;
      el.setAttribute('data-mhsys-ks', '');
      const titel = (seite.querySelector('.mhsys-produkt__titel, h1') || {}).textContent || '';
      const bild = seite.querySelector('[data-mhsys-galerie-bild]');
      el.innerHTML =
        '<div class="mhsys-ks__schleier" data-ks-zu></div>' +
        '<div class="mhsys-ks__panel" role="dialog" aria-modal="true" aria-labelledby="mhsys-ks-titel" tabindex="-1">' +
        '<div class="mhsys-ks__kopf">' +
        '<img class="mhsys-ks__bild" alt="" width="56" height="56" decoding="async">' +
        '<div class="mhsys-ks__name"><p class="mhsys-ks__titel" id="mhsys-ks-titel"></p>' +
        '<p class="mhsys-ks__preis"><b data-ks-preis></b> <span data-ks-variante></span></p></div>' +
        '<button type="button" class="mhsys-ks__zu mhsys-ueberlage__zu" data-ks-zu aria-label="Schließen">' +
        (window.mhsysZuSymbol || '×') +
        '</button></div>' +
        '<div class="mhsys-ks__inhalt"><div class="mhsys-ks__varianten mhsys-varianten"></div>' +
        '<div class="mhsys-ks__felder"></div><div class="mhsys-ks__genitiv"></div>' +
        '<p class="mhsys-ks__meldung" role="alert" hidden></p></div>' +
        '<div class="mhsys-ks__fuss"><button type="button" class="mhsys-kaufleiste__knopf mhsys-ks__kaufen" data-ks-kaufen></button></div>' +
        '</div>';
      el.querySelector('.mhsys-ks__titel').textContent = titel.trim();
      const img = el.querySelector('.mhsys-ks__bild');
      if (bild) img.src = bild.currentSrc || bild.src;
      else img.remove();
      el.querySelector('[data-ks-kaufen]').textContent = text('add_to_cart_leiste', 'Ins Kerberl legen');

      variantenKopieren(el.querySelector('.mhsys-ks__varianten'));
      felderSpiegeln(el.querySelector('.mhsys-ks__felder'));

      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-ks-zu]')) return zu();
        if (e.target.closest('[data-ks-kaufen]')) return kaufen();
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') return zu();
        if (e.key !== 'Tab') return;
        const ziele = [...el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea')].filter(
          (z) => z.offsetParent !== null
        );
        if (!ziele.length) return;
        if (e.shiftKey && document.activeElement === ziele[0]) {
          e.preventDefault();
          ziele[ziele.length - 1].focus();
        } else if (!e.shiftKey && document.activeElement === ziele[ziele.length - 1]) {
          e.preventDefault();
          ziele[0].focus();
        }
      });
      document.body.appendChild(el);
      if (window.mhsysSchuberAnmelden) window.mhsysSchuberAnmelden('kaufen', el, '[data-ks-zu]');

      // Nach dem Einlegen zu - der Warenkorb oeffnet sich danach wie gewohnt
      document.addEventListener('mhsys:warenkorb-geaendert', (e) => {
        const q = e.detail && e.detail.quelle;
        if (!el.hidden && (q === 'kaufformular' || q === 'produktoptionen')) zu(true);
      });
      // Preis und Variante stehen in der Kaufleiste schon aktuell (preiseSetzen) - von dort uebernehmen
      document.addEventListener('mhsys:variante', () => {
        if (!el.hidden) nachziehen();
      });
      // Genitiv-Frage und Fehlermeldungen des Kaufformulars in den Schuber holen
      if (poBox) {
        new MutationObserver(() => {
          if (el.hidden) return;
          const g = poBox.querySelector('.mhsys-po__genitiv');
          if (g) {
            genitivHerkunft = g.parentElement;
            el.querySelector('.mhsys-ks__genitiv').appendChild(g);
            const weiter = g.querySelector('button');
            if (weiter) {
              weiter.addEventListener('click', () => {
                setTimeout(() => {
                  if (document.documentElement.classList.contains('mhsys-wk-offen')) zu(true);
                }, 0);
              });
            }
            // ihr scrollIntoView hat die Seite dahinter bewegt
            window.scrollTo({ top: seitenY, behavior: 'instant' });
            const panel = el.querySelector('.mhsys-ks__panel');
            panel.scrollTo({ top: panel.scrollHeight, behavior: 'smooth' });
          }
        }).observe(poBox, { childList: true, subtree: true });
      }
      const fehlerFeld = formular.closest('mhsys-kaufformular, [data-mhsys-kaufformular]');
      const formMeldung = (fehlerFeld || formular.parentElement).querySelector('[data-mhsys-formular-fehler]');
      if (formMeldung) {
        new MutationObserver(() => {
          const m = el.querySelector('.mhsys-ks__meldung');
          m.textContent = formMeldung.textContent;
          m.hidden = formMeldung.hidden || !formMeldung.textContent.trim();
        }).observe(formMeldung, { childList: true, characterData: true, subtree: true, attributes: true });
      }
    }

    function variantenKopieren(ziel) {
      if (!gruppen.length) {
        ziel.remove();
        return;
      }
      gruppen.forEach((g, gi) => {
        const k = g.cloneNode(true);
        k.removeAttribute('data-mhsys-option');
        // Eigene Namen und Kennungen: die Kopie darf weder das Formular noch die Variantenlogik beruehren
        k.querySelectorAll('input').forEach((ein, i) => {
          const neu = 'mhsys-ks-' + gi + '-' + i;
          const lab = k.querySelector(`label[for="${CSS.escape(ein.id)}"]`);
          ein.dataset.ksOption = ein.dataset.mhsysOptionEingabe;
          delete ein.dataset.mhsysOptionEingabe;
          ein.name = 'mhsys-ks-' + gi;
          ein.id = neu;
          if (lab) lab.htmlFor = neu;
        });
        k.querySelectorAll('[data-mhsys-option-wert]').forEach((w) => w.removeAttribute('data-mhsys-option-wert'));
        // "Größentabelle": die Kopie traegt das Bereit-Zeichen des Originals, aber nicht seinen Horcher - der Tipp
        // verliess sonst die Produktseite (gefunden beim Pruefen am 08.10.2026). Neu verbinden, dann geht das Popup auf.
        k.querySelectorAll('[data-mhsys-popup-bereit]').forEach((a) => a.removeAttribute('data-mhsys-popup-bereit'));
        if (typeof window.mhsysPopupsVerbinden === 'function') window.mhsysPopupsVerbinden(k);
        k.addEventListener('change', (e) => {
          const ein = e.target;
          if (!ein.dataset.ksOption) return;
          const original = [...g.querySelectorAll('input[data-mhsys-option-eingabe]')].find((o) => o.value === ein.value);
          if (original && !original.checked) original.click();
          if (g === groessenGruppe) groesseGewaehlt = true;
          ziel.closest('.mhsys-ks').querySelector('.mhsys-ks__meldung').hidden = true;
        });
        ziel.appendChild(k);
        spiegel.push(() => {
          const orig = [...g.querySelectorAll('input[data-mhsys-option-eingabe]')];
          [...k.querySelectorAll('input')].forEach((ein, i) => {
            const o = orig[i];
            if (!o) return;
            ein.checked = o.checked;
            ein.toggleAttribute('data-mhsys-nicht-lieferbar', o.hasAttribute('data-mhsys-nicht-lieferbar'));
            const lo = g.querySelector(`label[for="${CSS.escape(o.id)}"]`);
            const lk = k.querySelector(`label[for="${CSS.escape(ein.id)}"]`);
            if (lo && lk) lk.className = lo.className;
          });
          const wo = g.querySelector('[data-mhsys-option-wert]');
          const wk = k.querySelector('.mhsys-varianten__wert');
          if (wo && wk) wk.textContent = wo.textContent;
        });
      });
    }

    function felderSpiegeln(ziel) {
      if (!pflichtFelder.length) {
        ziel.remove();
        return;
      }
      pflichtFelder.forEach((f, fi) => {
        const box = feldBox(f.schluessel);
        const zeile = document.createElement('div');
        zeile.className = 'mhsys-ks__feld';
        const id = 'mhsys-ks-feld-' + fi;
        const label = document.createElement('label');
        label.className = 'mhsys-ks__label';
        label.htmlFor = id;
        label.textContent = (f.label || f.schluessel) + ' *';
        zeile.appendChild(label);
        const fehler = document.createElement('p');
        fehler.className = 'mhsys-ks__fehler';
        fehler.hidden = true;
        let kopie;
        const original = box.querySelector('textarea, select, input:not([type="hidden"])');
        if (f.typ === 'text' || f.typ === 'textarea') {
          kopie = document.createElement(original.tagName === 'TEXTAREA' ? 'textarea' : 'input');
          if (kopie.tagName === 'INPUT') kopie.type = 'text';
          else kopie.rows = 2;
          kopie.className = 'mhsys-ks__eingabe';
          kopie.placeholder = (original.placeholder || '').replace(/\s*\*$/, '');
          if (original.maxLength > 0) kopie.maxLength = original.maxLength;
          kopie.autocomplete = 'off';
          kopie.enterKeyHint = 'done';
          kopie.addEventListener('input', () => {
            original.value = kopie.value;
            original.dispatchEvent(new Event('input', { bubbles: true }));
            fehlerSpiegeln();
          });
          kopie.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && kopie.tagName === 'INPUT') {
              e.preventDefault();
              kaufen();
            }
          });
        } else if (original.tagName === 'SELECT') {
          kopie = original.cloneNode(true);
          kopie.removeAttribute('name');
          kopie.className = 'mhsys-ks__eingabe mhsys-ks__auswahl';
          kopie.addEventListener('change', () => {
            original.value = kopie.value;
            original.dispatchEvent(new Event('change', { bubbles: true }));
            fehlerSpiegeln();
          });
        } else {
          // Radiogruppe als Knoepfe
          kopie = document.createElement('div');
          kopie.className = 'mhsys-ks__radios';
          kopie.setAttribute('role', 'radiogroup');
          [...box.querySelectorAll('input[type="radio"]')].forEach((r, ri) => {
            const lab = document.createElement('label');
            lab.className = 'mhsys-ks__radio';
            const ein = document.createElement('input');
            ein.type = 'radio';
            ein.name = 'mhsys-ks-r-' + fi;
            ein.value = r.value;
            ein.className = 'mhsys-vh';
            ein.addEventListener('change', () => {
              r.click();
              fehlerSpiegeln();
            });
            const span = document.createElement('span');
            span.textContent = (r.closest('label') || r.parentElement).textContent.trim();
            lab.append(ein, span);
            if (ri === 0) ein.id = id;
            kopie.appendChild(lab);
          });
        }
        if (kopie.tagName !== 'DIV') kopie.id = id;
        zeile.append(kopie, fehler);
        ziel.appendChild(zeile);

        function fehlerSpiegeln() {
          const o = box.querySelector('[data-mhsys-po-fehler]');
          const zeigt = o && !o.hidden && o.textContent.trim();
          fehler.textContent = zeigt ? o.textContent : '';
          fehler.hidden = !zeigt;
          zeile.classList.toggle('is-fehler', !!zeigt);
        }
        spiegel.push(() => {
          if (kopie.tagName === 'DIV') {
            const radios = [...box.querySelectorAll('input[type="radio"]')];
            [...kopie.querySelectorAll('input')].forEach((ein, i) => (ein.checked = !!(radios[i] && radios[i].checked)));
          } else if (document.activeElement !== kopie) {
            kopie.value = original.value;
          }
          fehlerSpiegeln();
        });
        zeile.dataset.ksFeld = String(fi);
      });
    }

    function nachziehen() {
      spiegel.forEach((f) => f());
      const preis = leiste.querySelector('[data-mhsys-leiste-preis]');
      const variante = leiste.querySelector('[data-mhsys-leiste-variante]');
      el.querySelector('[data-ks-preis]').textContent = preis ? preis.textContent.trim() : '';
      el.querySelector('[data-ks-variante]').textContent = variante && !variante.hidden ? variante.textContent.trim() : '';
      const kaufen = el.querySelector('[data-ks-kaufen]');
      const lk = leiste.querySelector('[data-mhsys-leiste-kaufen]');
      kaufen.disabled = !!(lk && lk.disabled);
      kaufen.textContent = lk && lk.disabled ? text('sold_out', 'Ausverkauft') : text('add_to_cart_leiste', 'Ins Kerberl legen');
    }

    function kaufen() {
      // Leere Pflichtfelder hier zeigen, nicht im Formular auf der Seite (dort wuerde es fokussieren und springen)
      let erstes = null;
      pflichtFelder.forEach((f, fi) => {
        const zeile = el.querySelector(`[data-ks-feld="${fi}"]`);
        if (!zeile) return;
        const fehler = zeile.querySelector('.mhsys-ks__fehler');
        if (leer(f)) {
          fehler.textContent = f.typ === 'text' || f.typ === 'textarea' ? 'Bitte ausfüllen.' : 'Bitte auswählen.';
          fehler.hidden = false;
          zeile.classList.add('is-fehler');
          if (!erstes) erstes = zeile.querySelector('input, textarea, select');
        }
      });
      if (erstes) {
        erstes.focus();
        return;
      }
      if (el.querySelector('.mhsys-ks__feld.is-fehler')) return; // Zeichenfehler aus der Produktoptionen-Pruefung
      groesseGewaehlt = true;
      seitenY = window.scrollY;
      seiteHalten();
      absenden();
      // Ist das Einlegen losgegangen, hat der Warenkorb sich schon geoeffnet (Platzhalter, Roadmap 1.70) - ab jetzt
      // zeigt er alles Weitere, auch eine Fehlermeldung. Der Schuber macht ihm Platz. Kam stattdessen die
      // Genitiv-Frage oder ein Zeichenfehler, bleibt er offen.
      if (document.documentElement.classList.contains('mhsys-wk-offen')) zu(true);
      // Die Pruefung im Formular kann den Fokus auf ein Feld der Seite gesetzt haben - die Seite bleibt, wo sie war
      requestAnimationFrame(() => window.scrollTo({ top: seitenY, behavior: 'instant' }));
    }

    /* Nach dem ersten Einlegen geht oben im Kaufbereich die Versandkosten-Box auf (snippets/mhsys-detail-trust.liquid,
       am Handy erst ab Warenkorbwert > 0, rund 98 px) - die Seite hinter Schuber und Warenkorb rutschte um so viel nach
       unten (Pruefbericht 08.10.2026). Chrome gleicht das per Scroll-Anchoring aus, Safari nicht. Deshalb ein paar
       Sekunden lang: waechst etwas ueber dem sichtbaren Inhalt, die Seite um genau so viel nachschieben. Wo der Browser
       schon ausgleicht, ist die Abweichung 0 und es passiert nichts; scrollt der Kunde selbst, gilt die neue Lage. */
    function seiteHalten() {
      const main = document.getElementById('MainContent') || document.querySelector('main');
      if (!main || typeof ResizeObserver === 'undefined') return;
      const anker = document
        .elementsFromPoint(window.innerWidth / 2, window.innerHeight / 2)
        .find((e) => e !== main && main.contains(e) && !e.closest('.mhsys-ks, mhsys-warenkorb, [data-mhsys-kaufleiste]'));
      if (!anker) return;
      let oben = anker.getBoundingClientRect().top;
      const merken = () => (oben = anker.getBoundingClientRect().top);
      const beobachter = new ResizeObserver(() => {
        if (!anker.isConnected) return;
        const d = anker.getBoundingClientRect().top - oben;
        if (Math.abs(d) > 1) window.scrollBy({ top: d, behavior: 'instant' });
        merken();
      });
      beobachter.observe(main);
      window.addEventListener('scroll', merken, { passive: true });
      setTimeout(() => {
        beobachter.disconnect();
        window.removeEventListener('scroll', merken);
      }, 6000);
    }

    function oeffnen(knopf) {
      if (!el) bauen();
      ausloeser = knopf;
      seitenY = window.scrollY;
      nachziehen();
      el.querySelector('.mhsys-ks__meldung').hidden = true;
      el.hidden = false;
      el.classList.add('is-offen');
      document.documentElement.classList.add('mhsys-overlay-offen');
      // Zuerst das erste leere Pflichtfeld, sonst die gewaehlte Groesse, sonst das Panel
      const leeres = pflichtFelder.findIndex(leer);
      const ziel =
        (leeres >= 0 && el.querySelector(`[data-ks-feld="${leeres}"] input, [data-ks-feld="${leeres}"] textarea, [data-ks-feld="${leeres}"] select`)) ||
        el.querySelector('.mhsys-ks__varianten input:checked') ||
        el.querySelector('.mhsys-ks__panel');
      ziel.focus({ preventScroll: true });
    }

    function zu(eingelegt) {
      if (!el || el.hidden) return;
      el.classList.remove('is-offen');
      el.hidden = true;
      document.documentElement.classList.remove('mhsys-overlay-offen');
      // Eine offene Genitiv-Frage zurueck an ihren Platz im Formular - dort bleibt sie stehen, bis sie beantwortet ist
      const g = el.querySelector('.mhsys-po__genitiv');
      if (g && genitivHerkunft && genitivHerkunft.isConnected) genitivHerkunft.appendChild(g);
      if (!eingelegt && ausloeser && ausloeser.isConnected) ausloeser.focus({ preventScroll: true });
    }

    return { noetig, oeffnen };
  }

  /* Versandhinweis der Kaufleiste (Roadmap 1.78): "noch … bis versandfrei" bzw. "mit diesem Artikel versandfrei".
     Grenze, Summe und Preis in Cent stehen am Element (Liquid); nachgezogen bei jeder Variante und nach jedem Einlegen. */
  function versandHinweisVerbinden(leiste) {
    const el = leiste.querySelector('[data-mhsys-leiste-versand]');
    if (!el) return;
    const grenze = Number(el.dataset.grenze);
    let summe = Number(el.dataset.summe);
    let preis = Number(el.dataset.preis);
    const geld = (cent) =>
      new Intl.NumberFormat('de-DE', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }).format(cent / 100);
    const schreiben = () => {
      const rest = grenze - summe;
      el.textContent =
        rest <= 0 ? 'versandkostenfrei' : preis >= rest ? 'mit diesem Artikel versandfrei' : 'noch ' + geld(rest) + ' bis versandfrei';
    };
    document.addEventListener('mhsys:variante', (e) => {
      const v = e.detail && e.detail.variante;
      if (v && typeof v.preis_zahl === 'number') {
        preis = v.preis_zahl;
        schreiben();
      }
    });
    document.addEventListener('mhsys:warenkorb-geaendert', () => {
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart.js')
        .then((r) => r.json())
        .then((k) => {
          summe = k.total_price;
          schreiben();
        })
        .catch(() => {});
    });
  }

  /* ------------------------------------------------------- "Weiter in …" (Roadmap 1.78)
     Inhaber 08.10.2026, Idee 7: wer aus einer Kollektion (oder der Suche) kommt, findet am Ende der Produktseite den
     vorigen und den naechsten Artikel dieser Liste - weiterstoebern ohne Umweg ueber Zurueck. Die Liste merkt
     das Skript der Kollektion (folgeMerken) beim Antippen einer Kachel (sessionStorage "mhsys-koll-folge": Handle, Name, Preis,
     Bild); hier wird nur gelesen, kein Abruf. Steht der Artikel nicht in der Liste (direkt gekommen, ueber "Passt
     dazu" weiter), gibt es den Block nicht. Er kommt ans Seitenende, unter alles Sichtbare - kein CLS. */
  function weiterInListe(seite) {
    const handle = seite.dataset.mhsysHandle;
    if (!handle || document.querySelector('[data-mhsys-weiter]')) return;
    let folge = null;
    try {
      folge = JSON.parse(window.sessionStorage.getItem('mhsys-koll-folge') || 'null');
    } catch (e) {
      return;
    }
    if (!folge || !Array.isArray(folge.liste)) return;
    const i = folge.liste.findIndex((x) => x.h === handle);
    if (i < 0) return;
    const vor = folge.liste[i - 1];
    const nach = folge.liste[i + 1];
    if (!vor && !nach) return;
    const wurzel = ((window.mhsysShop && window.mhsysShop.wurzelUrl) || '/').replace(/\/$/, '');
    const titel = folge.titel || 'dieser Liste';

    const el = (tag, klasse, text) => {
      const e = document.createElement(tag);
      if (klasse) e.className = klasse;
      if (text) e.textContent = text;
      return e;
    };
    const karte = (x, richtung, klasse) => {
      const a = el('a', 'mhsys-weiter__karte ' + klasse);
      a.href = wurzel + '/products/' + encodeURIComponent(x.h);
      if (x.b) {
        const img = el('img', 'mhsys-weiter__bild');
        img.src = x.b;
        img.alt = '';
        img.width = 300;
        img.height = 300;
        img.loading = 'lazy';
        a.appendChild(img);
      }
      const text = el('span', 'mhsys-weiter__text');
      text.appendChild(el('span', 'mhsys-weiter__richtung', richtung));
      text.appendChild(el('span', 'mhsys-weiter__name', x.t));
      if (x.p) text.appendChild(el('span', 'mhsys-weiter__preis', x.p));
      a.appendChild(text);
      return a;
    };

    const block = el('section', 'mhsys-weiter mhsys-breite');
    block.setAttribute('data-mhsys-weiter', '');
    block.setAttribute('aria-labelledby', 'mhsys-weiter-titel');
    block.appendChild(el('div', 'mhsys-label', 'Weiter stöbern'));
    const h = el('h2', 'mhsys-weiter__titel', 'Weiter in „' + titel + '“');
    h.id = 'mhsys-weiter-titel';
    block.appendChild(h);
    /* Immer zwei Karten, wenn die Liste es hergibt (Roadmap 4.29, Inhaber 09.10.2026: eine einzelne Karte "sieht
       unvollstaendig aus"): am Anfang der naechste und der danach, am Ende der vorige und - von vorn - der erste.
       Hat die Liste nur zwei Artikel, steht die eine Karte quer ueber die ganze Breite. */
    const karten = [];
    if (vor && nach) {
      karten.push([vor, '‹ Vorheriger'], [nach, 'Nächster ›']);
    } else if (nach) {
      karten.push([nach, 'Nächster ›']);
      if (folge.liste[i + 2]) karten.push([folge.liste[i + 2], 'Danach ›']);
    } else {
      karten.push([vor, '‹ Vorheriger']);
      if (i - 1 > 0) karten.push([folge.liste[0], 'Von vorn ›']);
    }
    const reihe = el('div', 'mhsys-weiter__reihe' + (karten.length === 1 ? ' mhsys-weiter__reihe--eine' : ''));
    karten.forEach((k, n) => reihe.appendChild(karte(k[0], k[1], n === 0 ? 'mhsys-weiter__karte--vor' : 'mhsys-weiter__karte--nach')));
    block.appendChild(reihe);
    // Der Name steht schon in der Ueberschrift - hier kurz (lange Kollektionsnamen liefen sonst ueber drei Zeilen)
    const alle = el('a', 'mhsys-weiter__alle', 'Zur ganzen Liste ›');
    alle.href = (folge.pfad || '/') + (folge.suche || '');
    block.appendChild(alle);

    const main = document.getElementById('MainContent') || document.querySelector('main');
    if (main) main.appendChild(block);
  }

  /* ------------------------------------------------------- "Passt dazu" laden */
  function passtDazuLaden() {
    const bereich = document.querySelector('[data-mhsys-passt-dazu]');
    if (!bereich || bereich.dataset.mhsysGeladen) return;
    const produktId = bereich.dataset.mhsysProduktId;
    const anzahl = bereich.dataset.mhsysAnzahl || 8;
    const sectionId = bereich.dataset.mhsysSection;
    if (!produktId || !sectionId) return;
    bereich.dataset.mhsysGeladen = '1';

    const wurzel = ((window.mhsysShop && window.mhsysShop.wurzelUrl) || '/').replace(/\/$/, '');
    const url = `${wurzel}/recommendations/products?section_id=${sectionId}&product_id=${produktId}&limit=${anzahl}`;

    /* Scheitert das Nachladen (Fehler, 429, leere Antwort), geht der Platzhalter weg - sonst blieb eine
       weiße Lücke von 340-450 px stehen (Rundum-Check 30.10.2026) */
    fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const neu = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mhsys-passt-dazu]') : null;
        if (!neu) { bereich.remove(); return; }
        neu.dataset.mhsysGeladen = '1';
        bereich.replaceWith(document.adoptNode(neu));
      })
      .catch((e) => { bereich.remove(); console.error('Passt dazu:', e); });
  }

  /* ------------------------------------------------------------------ Ausverkauft gesehen */
  /* Roadmap 3.104 (Inhaber 09.10.2026, mhsys docs/module/ausverkauft.md): ist die gewaehlte Variante nicht lieferbar,
     erfaehrt mhsys es - beim Laden und beim Wechsel auf eine nicht lieferbare Variante, je Seite und Variante einmal.
     Das zeigt Nachfrage, die wir nicht bedienen konnten. Vorbestellungen sind lieferbar und melden nicht. Nur die
     Produktseite, nicht die Schnellansicht. Gesendet im Leerlauf bzw. beim Wechsel, frühestens 300 ms nach dem Laden
     (sonst nimmt mhsys es nicht an), keepalive, Fehler bleiben still - nichts wartet auf die Antwort. */
  function ausverkauftMelden(seite) {
    const feld = seite.querySelector('[data-mhsys-variante-feld]');
    if (!feld || (window.Shopify && window.Shopify.designMode)) return;
    const gemeldet = new Set();
    const melden = (id) => {
      id = String(id || '');
      if (!/^\d+$/.test(id) || gemeldet.has(id)) return;
      gemeldet.add(id);
      const senden = () =>
        fetch('/apps/kunde?bereich=ausverkauft', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variante: id, _dauer: Math.round(performance.now()) }),
          credentials: 'same-origin',
          keepalive: true,
        }).catch(() => {});
      const warten = 350 - performance.now();
      if (warten > 0) setTimeout(senden, warten);
      else senden();
    };
    if (feld.dataset.mhsysLieferbar === 'false') {
      const los = () => melden(feld.value);
      if ('requestIdleCallback' in window) requestIdleCallback(los, { timeout: 3000 });
      else setTimeout(los, 1500);
    }
    document.addEventListener('mhsys:variante', (e) => {
      const d = e.detail || {};
      if (d.feld === feld && d.variante && d.variante.lieferbar === false) melden(d.variante.id);
    });
  }

  /* --------------------------------------------- Express-Schalter im Lagerstatus */
  /* Seit 01.11.2026 ein Schalter statt des Knopfs (Inhaber, Roadmap 1.131), wie der Schalter im Warenkorb: an legt Express
     SOFORT in den Warenkorb, aus nimmt ihn heraus (Inhaber 01.11.2026: „sofort, nicht erst mit dem Produkt“). Der Stand kommt
     aus dem Liquid (checked, wenn Express im Korb liegt) und wird nach dem Nachladen des Lagerstatus aus /cart.js nachgezogen
     (expressSchalterAbgleichen). Der Warenkorb oeffnet sich dabei nicht von selbst - leer mit nur dem Express darin war das
     verwirrend (Inhaber, 18.09.2026); liegt nur Express drin, sagt die Meldung, dass noch der Artikel fehlt. Eigene Kennung
     X-Mhsys-Warenkorb: der Warenkorb laedt nicht doppelt. */
  let expressImKorb = null;
  function expressSchalterAbgleichen(wurzel) {
    if (expressImKorb === null) return;
    (wurzel || document).querySelectorAll('[data-mhsys-express-schalter]').forEach((s) => {
      s.checked = expressImKorb;
      /* Lagerkasten neu (Roadmap 1.131): der Satz unter dem Schalter folgt dem Warenkorb */
      const meldung = s.closest('.mhsys-lagerbox__ex')?.querySelector('[data-mhsys-express-meldung]');
      if (meldung && !meldung.style.color) { meldung.textContent = expressImKorb ? '✓ Express liegt im Warenkorb.' : ''; meldung.style.display = ''; }
      /* … und Zusage und Datum oben rechnen mit Express (assets/mhsys-liefertage.js lagerbox) */
      const kasten = s.closest('[data-mhsys-lagerbox]');
      if (kasten && typeof kasten.mhsysLagerboxRechnen === 'function') kasten.mhsysLagerboxRechnen();
    });
  }
  window.mhsysExpressSchalterAbgleichen = expressSchalterAbgleichen;

  function expressMelden(schalter, text, fehler) {
    const meldung = schalter.closest('[data-mhsys-express]')?.querySelector('[data-mhsys-express-meldung]');
    if (!meldung) return;
    meldung.style.display = text ? 'block' : 'none';
    meldung.style.color = fehler ? '#c0392b' : '';
    meldung.textContent = text;
  }

  function expressVerbinden() {
    if (window.mhsysExpressSchalterVerbunden) return;
    window.mhsysExpressSchalterVerbunden = true;
    const kopf = { 'Content-Type': 'application/json', 'X-Mhsys-Warenkorb': '1' };

    document.addEventListener('change', (ev) => {
      const schalter = ev.target && ev.target.closest && ev.target.closest('[data-mhsys-express-schalter]');
      if (!schalter) return;
      const an = schalter.checked;
      const variante = String(schalter.dataset.variantId);
      schalter.disabled = true;
      expressMelden(schalter, '', false);
      const anfrage = an
        ? fetch('/cart/add.js', { method: 'POST', headers: kopf, body: JSON.stringify({ items: [{ id: variante, quantity: 1 }] }) })
        : fetch('/cart/change.js', { method: 'POST', headers: kopf, body: JSON.stringify({ id: variante, quantity: 0 }) });
      anfrage
        .then(async (r) => {
          const d = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(d.description || (an ? 'Express ließ sich nicht hinzufügen.' : 'Express ließ sich nicht entfernen.'));
        })
        .then(() => fetch('/cart.js').then((r) => r.json()))
        .then((korb) => {
          expressImKorb = an;
          expressSchalterAbgleichen();
          const andere = (korb.items || []).some((i) => String(i.variant_id) !== variante);
          if (window.mhsysWarenkorb) window.mhsysWarenkorb.aktualisieren(false);
          if (an) expressMelden(schalter, andere ? '✓ Express liegt im Warenkorb.' : '🚀 Express ist im Warenkorb – jetzt nur noch den Artikel dazulegen.', false);
        })
        .catch((err) => { schalter.checked = !an; expressMelden(schalter, err.message, true); })
        .finally(() => { schalter.disabled = false; });
    });
  }



  /* ------------------------------------------------ Versandkostenfrei-Box */
  /* Fortschritt bis versandkostenfrei (snippets/mhsys-detail-trust.liquid). Bis Roadmap 6.37 ein eigenes Skript im HTML.
     Liquid schreibt den Stand des Warenkorbs schon beim Ausliefern hinein; hier wird er bei jeder Aenderung am Warenkorb
     nachgezogen. Leer traegt die Box data-leer statt hidden: unter einem mhsys-Namen griffe sonst
     [class*='mhsys-'][hidden] (mhsys-weissblau.css, !important), und am Rechner soll sie auch leer stehen (mhsys-produkt.css). */
  function versandfreiVerbinden() {
    const box = document.getElementById('mhsys-versandfrei');
    if (!box || box.dataset.mhsysBereit) return;
    box.dataset.mhsysBereit = '1';
    const geschenkAb = Math.round(parseFloat(box.dataset.giftThreshold || '35') * 100);
    const frei = Math.round(parseFloat(box.dataset.shipThreshold || '69') * 100);
    const mitGeschenk = box.dataset.showGift === 'true';
    const geschenkZeile = box.querySelector('[data-line="gift"]');
    const versandZeile = box.querySelector('[data-line="ship"]');
    const fuellung = box.querySelector('[data-progress-fill]');
    const spur = box.querySelector('[role="progressbar"]');
    const geschenkMarke = box.querySelector('[data-milestone="gift"]');
    const versandMarke = box.querySelector('[data-milestone="ship"]');

    const geld = (cent) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try { return window.Shopify.formatMoney(parseInt(cent, 10)); } catch (e) {}
      }
      const betrag = parseInt(cent, 10) / 100;
      try { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(betrag); } catch (e) { return betrag.toFixed(2) + ' €'; }
    };
    const geschenkUnter = () => '<span class="mhsys-versandfrei__unter">ab ' + geld(geschenkAb) + ' gibt\'s a kloans Dankeschön!</span>';
    const versandUnter = () => '<span class="mhsys-versandfrei__unter">ab ' + geld(frei) + ' bei Lieferung innerhalb Deutschlands</span>';

    if (mitGeschenk && geschenkMarke) geschenkMarke.style.left = (geschenkAb / frei) * 100 + '%';
    if (versandMarke) {
      versandMarke.style.left = '100%';
      versandMarke.style.transform = 'translateX(-100%)';
    }

    const zeigen = (summe) => {
      summe = parseInt(summe || 0, 10);
      box.toggleAttribute('data-leer', summe <= 0);
      if (mitGeschenk && geschenkZeile) {
        if (summe >= geschenkAb) {
          geschenkZeile.innerHTML = '<strong>🎉 Gratis Geschenk freigeschaltet!</strong>' + geschenkUnter();
          geschenkMarke.classList.add('mhsys-versandfrei__marke--erreicht');
        } else {
          geschenkZeile.innerHTML = '<strong>Noch ' + geld(geschenkAb - summe) + ' bis zum Gratis-Geschenk</strong>' + geschenkUnter();
          geschenkMarke.classList.remove('mhsys-versandfrei__marke--erreicht');
        }
      }
      if (summe <= 0) {
        /* leer: am Handy versteckt, am Rechner sichtbar (gleiche Zeilenzahl wie gefuellt, damit nichts springt) */
        versandZeile.innerHTML = '<strong>Versandkostenfrei ab ' + Math.round(frei / 100) + ' €</strong><span class="mhsys-versandfrei__unter">bei Lieferung innerhalb Deutschlands</span>';
        versandMarke.classList.remove('mhsys-versandfrei__marke--erreicht');
      } else if (summe >= frei) {
        versandZeile.innerHTML = '<strong>🎉 Versandkostenfrei freigeschaltet!</strong>' + versandUnter();
        versandMarke.classList.add('mhsys-versandfrei__marke--erreicht');
      } else {
        versandZeile.innerHTML = '<strong>Noch ' + geld(frei - summe) + ' bis versandkostenfrei</strong>' + versandUnter();
        versandMarke.classList.remove('mhsys-versandfrei__marke--erreicht');
      }
      fuellung.style.width = Math.max(0, Math.min(100, (summe / frei) * 100)) + '%';
      spur.setAttribute('aria-valuenow', Math.round(summe / 100));
    };

    let takt = null;
    const nachziehen = () => {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error('Warenkorb'); return r.json(); })
        .then((korb) => zeigen(korb.total_price))
        .catch(() => {});
    };
    const spaeter = (ms) => { clearTimeout(takt); takt = setTimeout(nachziehen, ms); };
    nachziehen();

    /* Jede Aenderung am Warenkorb, egal von wem (Schublade, Kaufformular, Schnellkauf, Apps): fetch einmal umhuellen */
    if (!window.mhsysVersandfreiFetch) {
      window.mhsysVersandfreiFetch = true;
      const original = window.fetch;
      window.fetch = function () {
        const antwort = original.apply(this, arguments);
        try {
          const ziel = arguments[0] && (typeof arguments[0] === 'string' ? arguments[0] : arguments[0].url || '');
          const u = String(ziel || '').toLowerCase();
          if (u.includes('/cart/add') || u.includes('/cart/change') || u.includes('/cart/update') || u.includes('/cart/clear')) {
            antwort.then(() => { spaeter(150); spaeter(600); }).catch(() => {});
          }
        } catch (e) {}
        return antwort;
      };
    }
    document.addEventListener('submit', (ev) => {
      const ziel = ev.target && ev.target.getAttribute ? (ev.target.getAttribute('action') || '').toLowerCase() : '';
      if (ziel.includes('/cart/add')) { spaeter(300); spaeter(900); }
    }, true);
    document.addEventListener('click', (ev) => {
      if (ev.target && ev.target.closest && ev.target.closest('button[name="add"], [data-add-to-cart]')) { spaeter(300); spaeter(900); }
    }, true);
  }

  /* ------------------------------------------------------------------ Start */
  function starten() {
    /* Vor dem Ausstieg unten: Klappen haengen nicht an der Produktseite (08 D16). Die Overlays
       selbst verbindet assets/mhsys-popup.js sitewide von sich aus (eigener DOMContentLoaded). */
    akkordeonsVerbinden(document);

    const seite = document.querySelector('[data-mhsys-produkt-seite]');
    if (!seite) return;

    /* Kaufspalte (Roadmap 6.37): Express-Schalter, Versandkostenfrei-Box; die Liefertage rechnet assets/mhsys-liefertage.js */
    expressVerbinden();
    versandfreiVerbinden();

    /* Den offenen Artikel in den Verlauf eintragen (assets/mhsys-verlauf.js) */
    if (window.mhsysVerlauf && seite.dataset.mhsysHandle) {
      window.mhsysVerlauf.merken(seite.dataset.mhsysHandle);
    }
    weiterInListe(seite);

    if (!seite.dataset.mhsysBereit) {
      seite.dataset.mhsysBereit = '1';
      const galerieWurzel = seite.querySelector('[data-mhsys-galerie]');
      const galerie = galerieWurzel ? new MhsysGalerie(galerieWurzel) : null;
      new MhsysVarianten(seite, galerie);
      mengeVerbinden(seite);
      teilenVerbinden(seite);
      kaufleisteVerbinden(seite);
      ausverkauftMelden(seite);
    }
    passtDazuLaden();
  }

  /* Schnellansicht (Roadmap 3.25, assets/mhsys-schnellansicht.js): nur der Kaufteil fuer einen
     nachgeladenen Produktblock - Galerie, Variantenwahl, Menge/Mindestmenge, Info-Popups. Bewusst
     ohne Verlaufseintrag, Kaufleiste, Teilen und "Passt dazu": die gehoeren der Produktseite. */
  window.mhsysProduktKauf = (wurzel) => {
    if (!wurzel || wurzel.dataset.mhsysBereit) return;
    wurzel.dataset.mhsysBereit = '1';
    const galerieWurzel = wurzel.querySelector('[data-mhsys-galerie]');
    const galerie = galerieWurzel ? new MhsysGalerie(galerieWurzel) : null;
    new MhsysVarianten(wurzel, galerie);
    mengeVerbinden(wurzel);
    /* popupsVerbinden lebt jetzt in assets/mhsys-popup.js (sitewide, Roadmap 3.3) */
    if (typeof window.mhsysPopupsVerbinden === 'function') window.mhsysPopupsVerbinden(wurzel);
    akkordeonsVerbinden(wurzel);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', starten);
  else starten();
  document.addEventListener('shopify:section:load', starten);
})();
